import { bindable } from 'aurelia-framework';
import { } from '@toast-ui/editor';
const Editor = require("@toast-ui/editor");

/**
 * Wrapper for toast-ui/editor
 * 
 * Most of the bindable properties are directly passed through to the editor component.
 * Consult the documentation at https://nhn.github.io/tui.editor/latest/ToastUIEditorCore for more information.
 */
export class TuiEditor {
	editorContainer?: HTMLTextAreaElement;
	
	@bindable value?: string;
	
	/**
	 * Editor's height style value. Height is applied as border-box ex) '300px', '100%', 'auto'
	 */
	@bindable height?: string;

	/**
	 * Editor's min-height style value in pixel ex) '300px'
	 */
	@bindable minHeight?: string = '200px';

	/**
	 * Markdown editor's preview style (tab, vertical)
	 */
	@bindable previewStyle?: 'tab' | 'vertical';

	/**
	 * Highlight a preview element corresponds to the cursor position in the markdown editor
	 */
	@bindable previewHighlight?: boolean;

	/**
	 * Initial editor type (markdown, wysiwyg)
	 */
	@bindable initialEditType?: 'markdown' | 'wysiwyg' = 'wysiwyg';

	@bindable language?: string = 'en-US';

	/**
	 * whether use keyboard shortcuts to perform commands
	 */
	@bindable useCommandShortcut?: boolean = true;

	/**
	 * send hostname to google analytics
	 */
	@bindable usageStatistics?: boolean = false;

	/**
	 * Toolbar items
	 */
	@bindable toolbarItems?: (string | toastui.ToolbarButton)[];

	/**
	 * hide mode switch tab bar
	 */
	@bindable hideModeSwitch?: boolean = true;

	/**
	 * Array of plugins. A plugin can be either a function or an array in the form of function, options.
	 */
	@bindable plugins?: (toastui.Plugin | toastui.PluginInfo)[];

	/**
	 * Using extended Autolinks specified in GFM spec
	 */
	@bindable extendedAutolinks: toastui.ExtendedAutolinks;

	/**
	 * The placeholder text of the editable element.
	 */
	@bindable placeholder?: string;

	/**
	 * Attributes of anchor element that should be rel, target, hreflang, type
	 */
	@bindable linkAttribute?: toastui.LinkAttribute;

	/**
	 * Object containing custom renderer functions correspond to change markdown node to preview HTML or wysiwyg node
	 */
	@bindable customHTMLRenderer?: toastui.CustomHTMLRendererMap;

	/**
	 * Whether to use the specification of link reference definition
	 */
	@bindable referenceDefinition?: boolean;

	/**
	 * Custom HTML sanitizer
	 */
	@bindable customHTMLSanitizer?: toastui.CustomHTMLSanitizer;

	/**
	 * Whether use the front matter
	 */
	@bindable frontMatter?: boolean;
	
	@bindable imageCallback?: (file: Blob | File) => Promise<string>;

	private editor: toastui.Editor | null = null;
	
	attached() {
		const editorOptions: toastui.EditorOptions =  {
			el: this.editorContainer,
			height: this.height,
			minHeight: this.minHeight,
			initialValue: this.value,
			previewStyle: this.previewStyle,
			initialEditType: this.initialEditType,
			language: this.language,
			useCommandShortcut: this.useCommandShortcut,
			usageStatistics: false,
			toolbarItems: this.toolbarItems,
			hideModeSwitch: this.hideModeSwitch,
			placeholder: this.placeholder,
			customHTMLRenderer: this.customHTMLRenderer,
			referenceDefinition: this.referenceDefinition,
			customHTMLSanitizer: this.customHTMLSanitizer,
			previewHighlight: this.previewHighlight,
			frontMatter: this.frontMatter
		};
		
		for (const idx in editorOptions) {
			if (editorOptions[idx] === undefined) {
				delete editorOptions[idx];
			}
		}
		
		this.editor = new Editor(editorOptions);
		this.editor.on('change', this.editorValueChanged);
		if (this.imageCallback) {
			this.editor.addHook('addImageBlobHook', this.imageAdded);
		}
	}
	
	detached() {
		this.editor.remove();
		this.editor = null;
	}
	
	private editorValueChanged = (param: { source: toastui.SourceType | 'viewer'; data: MouseEvent }) => {
		this.value = this.editor.getMarkdown().replace(/^<br>$/gm, "");
	}
	
	private imageAdded = async (blob: Blob | File, readyCallback: (url: string, altText: string) => void) => {
		const url = await this.imageCallback(blob);
		
		readyCallback(url, '');
	};
}
